import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import styled from "styled-components";
import FullScreenDialog from "../../../../../../common/ui/FullScreenDialog";
import { VALUE_DOWN, VALUE_LEFT, VALUE_RIGHT, VALUE_UP } from "keycode-js";
import { throttle } from "throttle-debounce";
import { ZoomIn, ZoomOut, XLarge } from "@vitalsource/vstui-icons";
import { useIntl } from "react-intl";
import { convertSpacesToNonBreakingSpaces } from "../../../../../../common/utils/string";
import WhiteIconButton from "../../../../../../common/ui/WhiteIconButton";
import { isTouchDevice } from "../../../../../../common/utils/platform";
import ExpandableTruncator from "../../../../../../common/ui/ExpandableTruncator";
import { Button } from "@vitalsource/vstui";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import { clamp } from "../../../../../../common/utils/number";
import useIsTinyScreen from "../../../default-mobile/common/mobileBreakpoints";

const MoreLessThemedButton = createThemedComponent(Button, {
  Button_paddingHorizontal_medium: "0px",
  Button_minHeight_medium: "0px",
});

const Styled = {
  Dialog: styled(FullScreenDialog)`
    background: rgba(0, 0, 0, 0.9);
    width: 100%;
    height: 100%;
  `,
  MainContent: styled.div`
    width: 100%;
    height: 100%;
  `,
  Image: styled.img`
    object-fit: contain;
    opacity: 0;
    transition: opacity 0.1s 0.01s;
  `,
  Header: styled.div<{ open: boolean }>`
    touch-action: none;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.8);
    z-index: 10;
    min-height: 72px;

    font-size: 1.6rem;
    line-height: 1.9rem;
    color: white;

    transition: transform 200ms, opacity 200ms;
    opacity: ${(p) => (p.open ? 1 : 0)};
    transform: translateY(${(p) => (p.open ? 0 : "-100%")});
  `,
  Footer: styled.div<{ open: boolean }>`
    touch-action: none;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.8);
    z-index: 1;
    padding: 16px 0;

    svg {
      transform: scale(1.3);
    }

    & > * {
      position: relative;
      &:first-child {
        left: 50%;
        transform: translateX(-100%);
      }
      &:last-child {
        left: 50%;
        transform: translateX(-100%);
      }
    }
    transition: transform 200ms, opacity 200ms;
    opacity: ${(p) => (p.open ? 1 : 0)};
    transform: translateY(${(p) => (p.open ? 0 : "100%")});
  `,
  Truncator: styled(ExpandableTruncator)`
    width: 100%;
    padding: 16px 80px 30px 32px;
  `,
  MoreLessButton: styled(MoreLessThemedButton)`
    position: absolute;
    bottom: 8px;
    right: 80px;
  `,
  CloseButton: styled(WhiteIconButton)`
    position: absolute;
    top: 6px;
    right: 14px;
  `,
};

enum FitMode {
  WIDTH,
  HEIGHT,
  UNKNOWN,
}

const DEFAULT_FIT = FitMode.UNKNOWN;
const DEFAULT_TOOLS = true;
const DEFAULT_EXPANDED = false;
const DEFAULT_EXPANDABLE = false;
const MIN_SCALE = 1;
const MAX_SCALE = 8;

export default function ImageZoom({ src, open, onRequestClose, description }) {
  const transformRef = useRef<any>();
  const imageRef = useRef<HTMLImageElement>(null);
  const [fitMode, setFitMode] = useState(DEFAULT_FIT);
  const intl = useIntl();
  const [toolsShowing, setToolsShowing] = useState(DEFAULT_TOOLS);
  const [isExpanded, setIsExpanded] = useState(DEFAULT_EXPANDED);
  const [isExpandable, setIsExpandable] = useState(DEFAULT_EXPANDABLE);
  const [currentScale, setCurrentScale] = useState(MIN_SCALE);
  const [isDraggingToPan, setIsDraggingToPan] = useState(false);
  const isSmallScreen = useIsTinyScreen();

  function getContainer() {
    return imageRef.current!.closest<HTMLDivElement>(
      ".react-transform-wrapper"
    )!;
  }

  /**
   * onZoom handlers do not fire with external zoom changes
   * Checking the state immediately after an external zoom
   * does not provide the right value. Monitor to ensure
   * consistent correctness.
   */
  useEffect(() => {
    if (open) {
      const intervalId = window.setInterval(() => {
        const scale = transformRef.current?.state?.scale || 1;
        setCurrentScale(scale);
      }, 100);

      return () => clearTimeout(intervalId);
    }
  }, [open]);

  /**
   * Reset defaults between views
   */
  useEffect(() => {
    if (!open) {
      setFitMode(DEFAULT_FIT);
      setToolsShowing(DEFAULT_TOOLS);
      setIsExpanded(DEFAULT_EXPANDED);
      setIsExpandable(DEFAULT_EXPANDABLE);
    }
  }, [open]);

  const checkFit = useCallback(() => {
    const image = imageRef.current;
    if (image) {
      const imageAspectRatio = image.naturalWidth / image.naturalHeight;
      const container = getContainer();
      const areaAspectRatio = container.offsetWidth / container.offsetHeight;
      if (imageAspectRatio > areaAspectRatio) {
        setFitMode(FitMode.WIDTH);
      } else {
        setFitMode(FitMode.HEIGHT);
      }
    }
  }, []);

  const onImageLoad = useCallback(() => {
    checkFit();
  }, [checkFit]);

  useEffect(() => {
    if (open) {
      const onResize = throttle(200, () => {
        checkFit();
        if (transformRef.current) {
          // Is not inherently responsive, so reset on resize.
          // Might be nicer to use setTransform and maintain the existing
          // zoom and location, but maaaath
          transformRef.current.centerView(1, 0);
        }
      });
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
  }, [checkFit, open]);

  const onInit = useCallback(() => {
    if (isTouchDevice()) {
      const onClick = () => {
        setToolsShowing((showing) => !showing);
      };
      getContainer().addEventListener("click", onClick);
      return () => getContainer().removeEventListener("click", onClick);
    }
  }, []);

  const boundedPan = useCallback((deltaX, deltaY) => {
    const transform = transformRef.current;
    const image = imageRef.current;
    if (transform && image && image.complete) {
      const { positionX, positionY, scale } = transform.state;
      const containerWidth = getContainer().offsetWidth;
      const containerHeight = getContainer().offsetHeight;
      const imageWidth = image.offsetWidth * scale;
      const imageHeight = image.offsetHeight * scale;

      let finalX = positionX;
      let finalY = positionY;
      if (imageWidth > containerWidth) {
        finalX = clamp(positionX + deltaX, containerWidth - imageWidth, 0);
      }
      if (imageHeight > containerHeight) {
        finalY = clamp(positionY + deltaY, containerHeight - imageHeight, 0);
      }
      transform.setTransform(finalX, finalY);
    }
  }, []);

  useEffect(() => {
    if (open) {
      const step = 100;
      const onKey = throttle(200, (e) => {
        if (e.key === VALUE_RIGHT) {
          boundedPan(-step, 0);
        } else if (e.key === VALUE_LEFT) {
          boundedPan(step, 0);
        } else if (e.key === VALUE_UP) {
          boundedPan(0, step);
        } else if (e.key === VALUE_DOWN) {
          boundedPan(0, -step);
        }
      });
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
  }, [open, boundedPan]);

  /**
   * Fix issue with image jumping as it loads and centers. Toggling the opacity
   * as a layout effect mostly covers the jump. There's a little extra that is
   * covered by the css transition.
   */
  useLayoutEffect(() => {
    if (imageRef.current) {
      if (fitMode !== FitMode.UNKNOWN && imageRef.current.naturalHeight) {
        imageRef.current.style.opacity = "1";
        // Library does not auto-center on image load, though it will appear
        // to work if the load is fast
        transformRef.current.centerView(1, 0);
      } else {
        imageRef.current.style.opacity = "0";
      }
    }
  }, [fitMode]);

  const size: React.CSSProperties = {};
  if (fitMode === FitMode.HEIGHT) {
    size.height = "100%";
    size.flexDirection = "row";
  } else if (fitMode === FitMode.WIDTH) {
    size.width = "100%";
    size.flexDirection = "column";
  }

  return (
    <Styled.Dialog
      open={open}
      label={intl.formatMessage({ id: "imageZoom.title" })}
      onRequestClose={onRequestClose}
    >
      <TransformWrapper
        initialScale={1}
        centerOnInit
        ref={transformRef}
        onInit={onInit}
        minScale={MIN_SCALE}
        maxScale={MAX_SCALE}
        onPanningStart={() => setIsDraggingToPan(true)}
        onPanningStop={() => setIsDraggingToPan(false)}
        // key to ensure component is fully reset between views
        // including re-opening the same image
        key={open ? src : undefined}
      >
        <Styled.MainContent
          // Tools/title are always present. Reveal on keyboard focus changes
          // even if mouse is outside to avoid confusion.
          onFocus={() => setToolsShowing(true)}
          // Also reveal if you "click" an off-screen button
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.tagName.toLowerCase() === "button") {
              setToolsShowing(true);
            }
          }}
          onMouseEnter={
            isTouchDevice() ? undefined : () => setToolsShowing(true)
          }
          onMouseLeave={
            isTouchDevice() ? undefined : () => setToolsShowing(false)
          }
        >
          <Styled.Header open={toolsShowing}>
            <Styled.Truncator
              isExpanded={isExpanded}
              numLines={isSmallScreen ? 4 : 2}
              onExpandableStatus={(isExpandable) =>
                setIsExpandable(isExpandable)
              }
            >
              {description}
            </Styled.Truncator>
            <Styled.CloseButton
              onClick={onRequestClose}
              label={intl.formatMessage({ id: "base.close" })}
              icon={<XLarge />}
            />
            {isExpandable && (
              <Styled.MoreLessButton
                naked
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {intl.formatMessage({
                  id: isExpanded ? "base.less" : "base.more",
                })}
              </Styled.MoreLessButton>
            )}
          </Styled.Header>
          <TransformComponent
            wrapperStyle={{
              width: "100%",
              height: "100%",
              cursor: isDraggingToPan ? "grabbing" : "default",
            }}
            contentStyle={size}
          >
            <Styled.Image
              src={src}
              alt=""
              onLoad={onImageLoad}
              ref={imageRef}
              style={size}
            />
          </TransformComponent>
          <Styled.Footer open={toolsShowing}>
            <WhiteIconButton
              onClick={() => {
                transformRef.current.zoomOut();
              }}
              label={convertSpacesToNonBreakingSpaces(
                intl.formatMessage({ id: "format.zoomOut" })
              )}
              disabled={currentScale === MIN_SCALE}
              icon={<ZoomOut />}
            />
            <WhiteIconButton
              onClick={() => {
                transformRef.current.zoomIn();
              }}
              label={convertSpacesToNonBreakingSpaces(
                intl.formatMessage({ id: "format.zoomIn" })
              )}
              disabled={currentScale === MAX_SCALE}
              icon={<ZoomIn />}
            />
          </Styled.Footer>
        </Styled.MainContent>
      </TransformWrapper>
    </Styled.Dialog>
  );
}
